import { CategoryInterface, ProductInterface } from '../../../services/exports/Interfaces';
import { WithActualPrice } from '../../../providers/MenuProvider';
import {
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useAppState from '../../../hooks/global/useAppState';
import useMenu from '../../../hooks/menu/useMenu';
import { useSearchParams } from 'react-router-dom';
import collect from 'collect.js';
import { MENU_TAB_CONTENT_ID } from '../../../modules/mobile/pages/tabs/menu';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import usePageHeader from '../../../hooks/navigation/usePageHeader';
import useScreenType from '../../../hooks/utility/useScreenType';

interface MenuCategoryListContextInterface {
  filteredCategories: CategoryInterface<WithActualPrice<ProductInterface>>[];
  focusedCategoryId: number;
  setFocusedCategoryId: Dispatch<SetStateAction<number>>;
  menuBarContainerRef: MutableRefObject<HTMLDivElement>;
  menuBarRef: MutableRefObject<HTMLDivElement>;
  jumpToCategory: (id?: number, behavior?: ScrollBehavior) => void;
}

export const MenuCategoryListCtx = createContext<MenuCategoryListContextInterface>(null);

export const MenuCategoryListProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isMobileApp } = useMobileApp();
  const { isDesktop } = useScreenType();
  const { pageHeaderHeight } = usePageHeader();
  const menuBarContainerRef = useRef<HTMLDivElement>();
  const menuBarRef = useRef<HTMLDivElement>();
  const { menu } = useAppState();
  const { categories } = useMenu();
  const [searchParams] = useSearchParams();
  const searchString = searchParams.get('search');
  const filteredCategories = useMemo(() => {
    return searchParams.has('search') && searchString.length
      ? categories.filter((category) =>
          category.products.some((product) => new RegExp(searchString, 'i').test(product.name)),
        )
      : categories;
  }, [categories, searchString, searchParams]);
  const [focusedCategoryId, setFocusedCategoryId] = useState<number>(
    menu?.activeCategoryId ?? collect(filteredCategories)?.first()?.id,
  );
  const jumpToCategory = (id?: number, behavior: ScrollBehavior = 'smooth'): void => {
    const category = document.getElementById(`category-id-${id}`);

    if (isMobileApp) {
      const scrollableEl = document.getElementById(MENU_TAB_CONTENT_ID) as HTMLIonContentElement;

      void scrollableEl.scrollToPoint(undefined, category.offsetTop - menuBarRef.current.scrollHeight - 12, 600);

      return;
    }

    if (!isDesktop) {
      const elementPosition = category.getBoundingClientRect().top;
      const headerHeight = document.getElementById('page-header').scrollHeight || 0;
      const offset = headerHeight > 0 ? headerHeight + 28 : pageHeaderHeight.desktop + 28;
      const top = elementPosition + window.scrollY - offset;

      return window.scrollTo({
        left: 0,
        top,
        behavior,
      });
    }

    const elementPosition = category.getBoundingClientRect().top;
    const menuBarHeight = menuBarRef.current?.scrollHeight || 0;
    const offset = menuBarHeight > 0 ? menuBarHeight + 16 : pageHeaderHeight.desktop + 16;
    const top = elementPosition + window.scrollY - offset;

    window.scrollTo({
      left: 0,
      top,
      behavior,
    });
  };

  const providerValue = useMemo(
    () => ({
      filteredCategories,
      focusedCategoryId,
      setFocusedCategoryId,
      menuBarContainerRef,
      menuBarRef,
      jumpToCategory,
    }),
    [filteredCategories, focusedCategoryId, jumpToCategory],
  );

  return <MenuCategoryListCtx.Provider value={providerValue}>{children}</MenuCategoryListCtx.Provider>;
};

export const useMenuCategoryList = () => useContext(MenuCategoryListCtx);
